"use client";

import { FooterProps } from "@/types/component.types";
import { usePathname } from "next/navigation";
import { useState, useRef, useEffect } from "react";
import Button from "@/components/ui/Button";
import SafeContactButton from "@/components/security/SafeContactButton";
import Text from "@/components/ui/Text";
import Headline from "@/components/ui/Headline";
import Link from "next/link";
import Image from "next/image";
import classNames from "classnames";
import { motion } from "framer-motion";
import PhoneIcon from "@/components/icons/Phone.icon";
import MailIcon from "@/components/icons/Mail.icon";
import ArrowUpIcon from "@/components/icons/ArrowUp.icon";

export default function Footer({ blok }: { blok?: FooterProps }) {
    
    const pathname = usePathname();
    const segments = pathname.split("/");
    const currentLang = segments[1] && segments[1].length === 2 ? segments[1] : "de";
    const isTetris = pathname.includes("/tetris");

    // state for the btt animation
    const [isLaunching, setIsLaunching] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [rocketPos, setRocketPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef<HTMLButtonElement>(null);

    const encodedEmail = blok?.email ? Buffer.from(blok.email).toString("base64") : null;
    const encodedPhone = blok?.phone ? Buffer.from(blok.phone).toString("base64") : null;

    const scrollToTop = () => {
        if (btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            // capture exact center of the current button
            setRocketPos({
                top: rect.top,
                left: rect.left,
            });
        }
        
        setIsLaunching(true);
        setIsExiting(false);
        
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY <= 5 && isLaunching) {
                setIsExiting(true);
                setTimeout(() => {
                    setIsLaunching(false);
                    setIsExiting(false);
                }, 800); 
            }
        };

        const handleInterrupt = () => {
            if (isLaunching && window.scrollY > 100) {
                setIsLaunching(false);
                setIsExiting(false);
            }
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("wheel", handleInterrupt);
        window.addEventListener("touchmove", handleInterrupt);

        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("wheel", handleInterrupt);
            window.removeEventListener("touchmove", handleInterrupt);
        };
    }, [isLaunching]);

    return (
        <footer className="container relative pt-20">
            {isLaunching && (
                <div 
                    className={classNames(
                        "fixed z-9999 pointer-events-none p-3 transition-all duration-700 ease-in-out",
                        {
                            "opacity-0 scale-150": isExiting,
                            "opacity-100 scale-100": !isExiting
                        }
                    )}
                    style={{ 
                        top: isExiting ? "-100px" : `${rocketPos.top}px`, 
                        left: `${rocketPos.left}px` 
                    }}
                >
                    <ArrowUpIcon />
                    
                    {/* subtle propulsion glow that appears only during flight */}
                    {!isExiting && (
                        <div className="absolute top-full left-1/2 -translate-x-1/2 w-1 h-8 bg-linear-to-b dark:from-beige from-red to-transparent blur-sm animate-pulse" />
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 xl:mb-12 relative z-10">
                <div className="flex flex-col md:items-start items-center justify-center">
                    <div className="flex items-center flex-col md:flex-row gap-4 mb-4">
                        <Link
                            className="group block"
                            href={`/${currentLang}`}
                            draggable={false}
                        >
                            {blok?.logo?.[0]?.src?.filename && (
                                <motion.div
                                    className="relative z-50 custom-cursor-hover xl:hover:cursor-none"
                                    drag
                                    dragConstraints={{ top: 0, left: 0, right: 0, bottom: 0 }}
                                    dragElastic={0.8}
                                    dragTransition={{ bounceStiffness: 600, bounceDamping: 15 }}
                                    whileDrag={{ scale: 1.2, zIndex: 50, cursor: "grabbing" }}
                                >
                                    <Image
                                        className="h-12 w-12 object-contain group-hover:scale-110 group-hover:opacity-70 duration-300"
                                        src={blok.logo[0].src.filename}
                                        alt={blok.logo[0].alt}
                                        width={48}
                                        height={48}
                                        loading="eager"
                                        draggable={false}
                                    />
                                </motion.div>
                            )}
                        </Link>
                        <Headline
                            className="text-center md:text-start md:whitespace-nowrap"
                            text={blok?.name || "Your Name"}
                            level="h2"
                            tag="h2"
                        />
                    </div>
                    
                    {(encodedEmail || encodedPhone) && (
                        <div className="flex flex-col md:items-start items-center gap-y-2">
                            {encodedEmail && (
                                <SafeContactButton 
                                    className="text-center md:text-start" 
                                    type="mailto"
                                    encodedValue={encodedEmail}
                                    icon={<MailIcon />}
                                />
                            )}
                            {encodedPhone && (
                                <SafeContactButton 
                                    className="text-center md:text-start" 
                                    type="tel"
                                    encodedValue={encodedPhone}
                                    icon={<PhoneIcon />}
                                />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-col md:items-end items-center justify-between gap-6 md:gap-0">
                    {/* btt btn */}
                    <button 
                        ref={btnRef}
                        onClick={scrollToTop}
                        aria-label="Back to top"
                        className={classNames(
                            "relative md:mt-3 xl:mt-4 p-3 rounded-full transition-all cursor-pointer duration-300",
                            // background & text color switching
                            "bg-beige text-[#171717] dark:bg-black dark:text-[#fcf4e1]",
                            // base shadows (light/dark)
                            "shadow-[0_0_10px_0_rgba(23,23,23,0.5)] dark:shadow-[0_0_10px_0_rgba(252,244,225,0.5)]",
                            // hover shadows (light/dark)
                            "hover:shadow-[0_0_6px_0_rgba(23,23,23,0.5)] dark:hover:shadow-[0_0_6px_0_rgba(252,244,225,0.5)]",
                            // inner shadows base
                            "after:content-[''] after:absolute after:inset-0 after:rounded-full after:duration-300",
                            "after:shadow-[inset_0_0_4px_0_rgba(23,23,23,0.5)] dark:after:shadow-[inset_0_0_4px_0_rgba(252,244,225,0.5)]",
                            "hover:after:shadow-[inset_0_0_10px_0_rgba(23,23,23,0.5)] dark:hover:after:shadow-[inset_0_0_10px_0_rgba(252,244,225,0.5)]",
                            {
                                "opacity-0 scale-50": isLaunching,
                                "opacity-100 scale-100": !isLaunching
                            }
                        )}
                    >
                        <ArrowUpIcon />
                    </button>
                    
                    <Button
                        className="max-w-[350px]"
                        variant="secondary"
                        href={isTetris ? `/${currentLang}` : `/${currentLang}/tetris`}
                    >
                        {currentLang === "de"
                            ? (isTetris ? "Jeder braucht mal eine Pause 🍃" : "Bereit für eine Herausforderung?")
                            : (isTetris ? "Everyone needs a break sometimes 🍃" : "Ready for a challenge?")
                        }
                    </Button>
                </div>
            </div>

            <div className="py-6 border-t border-gray-100 dark:border-zinc-800 flex flex-col md:flex-row justify-between items-center gap-3">
                <Text
                    className="md:order-1 text-center"
                    color="text-black dark:text-beige"
                    variant="small"
                >
                    &copy; {new Date().getFullYear()} {blok?.copyright_text || "All rights reserved."}
                </Text>

                <div className="flex items-center gap-6 order-1 md:order-2">
                    {blok?.privacy?.[0]?.link && (
                        <Button
                            variant="tertiary"
                            href={`/${currentLang}/${(blok.privacy[0].link.url || blok.privacy[0].link.cached_url).replace(/^\//, "").replace(/^(en|de)\//, "")}`}
                        >
                            {blok?.privacy[0].label || "Privacy Policy"}
                        </Button>
                    )}
                    {blok?.imprint?.[0]?.link && (
                        <Button
                            variant="tertiary"
                            href={`/${currentLang}/${(blok.imprint[0].link.url || blok.imprint[0].link.cached_url).replace(/^\//, "").replace(/^(en|de)\//, "")}`}
                        >
                            {blok?.imprint[0].label || "Imprint"}
                        </Button>
                    )}
                </div>
            </div>
        </footer>
    );
};